import {
  ChatbotMessage,
  chatbotMessageFrom,
  chatbotMessageState,
} from "@ac-expo/api-types";
import { RagFaqPlatform } from "@prisma/client";
import { uniqBy } from "lodash";
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from "react";

type ChatbotContextType = {
  showWindow: boolean;
  setShowWindow: Dispatch<SetStateAction<boolean>>;
  showWidget: boolean;
  setShowWidget: Dispatch<SetStateAction<boolean>>;
  isBotWriting: boolean;
  messages: ChatbotMessage[];
  sendMessage: (content: string) => void;
};
type WebSocketContextType = {
  isConnected: boolean;
  sendMessage: <T extends object>(
    event: string,
    data: any,
    callback?: (res: T) => void,
  ) => void;
  subscribe: <T extends object>(
    event: string,
    callback: (data: T) => void,
  ) => () => void;
};
type ChatbotMessageResponse =
  | {
      status: "success";
      data: {
        id: string;
      };
    }
  | { status: "error" };

export const ChatbotContext = createContext({} as ChatbotContextType);

export type ChatbotProviderProps = {
  children: React.ReactNode;
  ws: WebSocketContextType;
  messages: ChatbotMessage[];
  app: RagFaqPlatform;
};
export const ChatbotProvider = ({
  children,
  ws,
  app,
  messages: existingMessages,
}: ChatbotProviderProps) => {
  const [showWindow, setShowWindow] = useState(false);
  const [showWidget, setShowWidget] = useState(true);
  const [isBotWriting, setIsBotWriting] = useState(false);
  const { isConnected, sendMessage: sendWsMessage, subscribe } = ws;
  const [messages, setMessages] = useState<ChatbotMessage[]>([
    greetingMessage,
    ...existingMessages,
  ]);

  useEffect(() => {
    setMessages([greetingMessage, ...existingMessages]);
  }, [existingMessages]);

  useEffect(() => {
    if (!isConnected) {
      return;
    }

    const handleNewMessage = (data: ChatbotMessage) => {
      setMessages((prev) =>
        uniqBy([...prev, { ...data, state: chatbotMessageState.sent }], "id"),
      );
      setIsBotWriting(false);
    };
    const unsubscribe = subscribe("new-chatbot-message", handleNewMessage);

    return () => {
      unsubscribe();
    };
  }, [subscribe, isConnected]);

  const sendChatbotMessage = (content: string) => {
    const newMessage = {
      id: getFakeId(),
      content,
      app,
      createdAt: new Date(),
      knowledgeBaseUsed: false,
    };
    setMessages((prev) => [
      ...prev,
      {
        ...newMessage,
        state: chatbotMessageState.sending,
        from: chatbotMessageFrom.user,
      },
    ]);
    setIsBotWriting(true);

    sendWsMessage<ChatbotMessageResponse>(
      "new-chatbot-message",
      newMessage,
      (response) => {
        if (response.status === "error") {
          setMessages((prev) => prev.filter((m) => m.id !== newMessage.id));
          console.error("Error sending message to WebSocket");
        } else {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === newMessage.id
                ? {
                    ...m,
                    id: response.data.id,
                    state: chatbotMessageState.sent,
                    from: chatbotMessageFrom.user,
                  }
                : m,
            ),
          );
        }
      },
    );
  };

  return (
    <ChatbotContext.Provider
      value={{
        setShowWindow,
        showWindow,
        showWidget,
        setShowWidget,
        isBotWriting,
        messages: messages,
        sendMessage: sendChatbotMessage,
      }}
    >
      {children}
    </ChatbotContext.Provider>
  );
};

export const useChatbot = () => {
  const context = React.useContext(ChatbotContext);

  if (!context) {
    throw new Error("useChatbot must be used within a ChatbotProvider");
  }

  return context;
};

const greetingMessage = {
  id: getFakeId(),
  content: "Comment puis-je vous aider aujourd'hui?",
  createdAt: new Date(),
  state: chatbotMessageState.sent,
  from: chatbotMessageFrom.bot,
  knowledgeBaseUsed: false,
};

function getFakeId() {
  return Date.now().toString();
}
